import { createStore } from '@ngneat/elf';
import {
  addEntities,
  deleteEntities,
  getAllEntities,
  selectAllEntities,
  setEntities,
  upsertEntities,
  withEntities,
} from '@ngneat/elf-entities';
import { Injectable } from '@angular/core';
import { Observable } from 'rxjs';
import { RoomSchedule } from '../shared/models/schedule';
import type { RoomDescriptor } from '../core/services/realtime.service';

const ROOMS_STORE_NAME = 'andorRooms';

/**
 * A room persisted on the server for this domain. Mirrors the server's
 * `RoomDescriptor`. `roomId` is the elf entity key (server-minted); `name` is
 * the human display name (not unique); `memberCount` is the live number of
 * members currently attached.
 */
export type Room = {
  roomId: string;
  name: string;
  isPrivate: boolean;
  hasPassword: boolean;
  /**
   * Hidden rooms (e.g. instant meetings) are never pushed in the server's
   * `rooms-snapshot`/deltas, so they only ever land in this store when the
   * client resolves them by id (e.g. an invite link) via `AppService.lookupRoom`.
   * Browse lists must filter these out (`!isHidden`); they remain joinable and
   * usable for schedule/meta display. Defaults to `false`.
   */
  isHidden: boolean;
  memberCount: number;
  /** Optional meeting schedule synced from the server (absent = unscheduled). */
  schedule?: RoomSchedule;
  /**
   * Live recording ownership synced from the server (mirrors
   * `RoomDescriptor.recording`). Present while the room is being recorded;
   * `null`/absent otherwise. `startedBy` is the andorUUID of the user who
   * started it — only they may stop it.
   */
  recording?: {
    startedBy: string;
    startedByName: string;
    startedAt: number;
  } | null;
};

/**
 * Normalise a wire `RoomDescriptor` into the internal {@link Room} shape.
 * Shared by every path that receives one — the WS deltas/snapshot
 * (`AppService`) and the HTTP room endpoints (`RoomService`) — so they cannot
 * drift apart.
 */
export function roomFromDescriptor(r: RoomDescriptor): Room {
  return {
    roomId: r.roomId,
    name: r.name ?? r.roomId,
    isPrivate: r.isPrivate ?? false,
    hasPassword: r.hasPassword ?? false,
    isHidden: r.isHidden ?? false,
    memberCount: r.memberCount ?? 0,
    schedule: r.schedule,
    // Normalise absent -> null so a `room-updated` that omits `recording`
    // clears any prior ownership rather than leaving it stale.
    recording: r.recording ?? null,
  };
}

const roomsStore = createStore(
  { name: ROOMS_STORE_NAME },
  withEntities<Room, 'roomId'>({ idKey: 'roomId' })
);

@Injectable({
  providedIn: 'root',
})
export class RoomsStore {
  readonly store = roomsStore;

  readonly rooms$: Observable<Room[]> = this.store.pipe(selectAllEntities());

  get rooms(): Room[] {
    return this.store.query(getAllEntities());
  }

  /** Replace the full set — used to apply the `rooms-snapshot` on WS connect. */
  setAll(rooms: Room[]): void {
    this.store.update(setEntities(rooms));
  }

  /** Idempotent add — used for `room-added` deltas. */
  add(room: Room): void {
    this.store.update(upsertEntities([room]));
  }

  remove(roomId: string): void {
    this.store.update(deleteEntities(roomId));
  }
}
